import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition, Tab } from '@headlessui/react';
import { XMarkIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';

// Define the schema for budget adjustment data
const budgetAdjustmentSchema = z.object({
  budget_item_id: z.string().uuid(),
  previous_amount: z.number().nonnegative(),
  new_amount: z.number().nonnegative(),
  adjustment_date: z.string().min(1, { message: "Adjustment date is required" }),
  reason: z.string().min(1, { message: "Reason is required" })
});

type BudgetAdjustmentData = z.infer<typeof budgetAdjustmentSchema>;

interface BudgetAdjustmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  budgetItem: {
    id: string;
    category: string;
    description: string;
    allocated_amount: number;
    spent_amount: number;
  } | null;
  adjustmentHistory: Array<{
    id: string;
    previous_amount: number;
    new_amount: number;
    adjustment_date: string;
    reason: string;
  }>;
  onSuccess: () => void;
}

export default function BudgetAdjustmentModal({
  isOpen,
  onClose,
  budgetItem,
  adjustmentHistory,
  onSuccess
}: BudgetAdjustmentModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('adjust');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch
  } = useForm<BudgetAdjustmentData>({
    resolver: zodResolver(budgetAdjustmentSchema),
    defaultValues: {
      budget_item_id: '',
      previous_amount: 0,
      new_amount: 0,
      adjustment_date: new Date().toISOString().split('T')[0],
      reason: ''
    }
  });

  const newAmount = watch('new_amount');
  const previousAmount = watch('previous_amount');

  useEffect(() => {
    if (budgetItem) {
      // Set form values from budget item
      setValue('budget_item_id', budgetItem.id);
      setValue('previous_amount', budgetItem.allocated_amount);
      setValue('new_amount', budgetItem.allocated_amount);
      setValue('adjustment_date', new Date().toISOString().split('T')[0]);
      setValue('reason', '');
    } else {
      // Reset form
      reset({
        budget_item_id: '',
        previous_amount: 0,
        new_amount: 0,
        adjustment_date: new Date().toISOString().split('T')[0],
        reason: ''
      });
    }
  }, [budgetItem, reset, setValue]);

  const onSubmit = async (data: BudgetAdjustmentData) => {
    try {
      setIsSubmitting(true);
      setError('');

      if (!budgetItem) {
        throw new Error('Budget item not found');
      }

      // Create adjustment record
      const { error: adjustmentError } = await supabase
        .from('budget_adjustments')
        .insert([{
          budget_item_id: data.budget_item_id,
          previous_amount: data.previous_amount,
          new_amount: data.new_amount,
          adjustment_date: data.adjustment_date,
          reason: data.reason
        }]);

      if (adjustmentError) throw adjustmentError;

      // Update budget item with new allocated amount
      const { error: updateError } = await supabase
        .from('budget_items')
        .update({ allocated_amount: data.new_amount })
        .eq('id', budgetItem.id);

      if (updateError) throw updateError;

      toast.success('Budget adjusted successfully');
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error adjusting budget:', err);
      setError(err.message || 'An error occurred while adjusting the budget');
      toast.error('Failed to adjust budget');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-lg transform rounded-lg bg-white shadow-xl transition-all">
                <Tab.Group selectedIndex={activeTab === 'adjust' ? 0 : 1} onChange={(index) => setActiveTab(index === 0 ? 'adjust' : 'history')}>
                  <div className="border-b border-gray-200">
                    <Tab.List className="flex">
                      <Tab className={({ selected }) =>
                        `w-1/2 py-4 px-1 text-center border-b-2 font-medium text-sm ${
                          selected
                            ? 'border-golf-orange text-golf-orange'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`
                      }>
                        Adjust Budget
                      </Tab>
                      <Tab className={({ selected }) =>
                        `w-1/2 py-4 px-1 text-center border-b-2 font-medium text-sm ${
                          selected
                            ? 'border-golf-orange text-golf-orange'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`
                      }>
                        Adjustment History
                      </Tab>
                    </Tab.List>
                  </div>

                  <Tab.Panels>
                    <Tab.Panel>
                      <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="px-4 py-5 sm:p-6">
                          {error && (
                            <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
                              <div className="flex">
                                <ExclamationCircleIcon className="h-5 w-5 text-red-500 mr-2" />
                                <span>{error}</span>
                              </div>
                            </div>
                          )}

                          <input
                            type="hidden"
                            {...register('budget_item_id')}
                          />

                          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                            <div className="sm:col-span-3">
                              <label htmlFor="previous_amount" className="block text-sm font-medium text-gray-700">
                                Current Amount
                              </label>
                              <div className="mt-1">
                                <input
                                  type="number"
                                  step="0.01"
                                  {...register('previous_amount', { valueAsNumber: true })}
                                  disabled
                                  className="block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-golf-orange focus:ring-golf-orange sm:text-sm"
                                />
                              </div>
                            </div>

                            <div className="sm:col-span-3">
                              <label htmlFor="new_amount" className="block text-sm font-medium text-gray-700">
                                New Amount <span className="text-red-500">*</span>
                              </label>
                              <div className="mt-1">
                                <input
                                  type="number"
                                  step="0.01"
                                  {...register('new_amount', { valueAsNumber: true })}
                                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange sm:text-sm"
                                />
                              </div>
                              {errors.new_amount && (
                                <p className="mt-2 text-sm text-red-600">{errors.new_amount.message}</p>
                              )}
                            </div>

                            <div className="sm:col-span-6">
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-500">Difference:</span>
                                <span className={`text-sm font-medium ${
                                  newAmount > previousAmount ? 'text-green-600' : newAmount < previousAmount ? 'text-red-600' : 'text-gray-600'
                                }`}>
                                  {newAmount > previousAmount ? '+' : ''}
                                  {formatCurrency(newAmount - previousAmount)}
                                  {' '}
                                  ({newAmount > previousAmount ? '+' : ''}
                                  {((newAmount - previousAmount) / (previousAmount || 1) * 100).toFixed(1)}%)
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                                <div
                                  className={`rounded-full h-1.5 ${
                                    newAmount > previousAmount ? 'bg-green-500' : 'bg-red-500'
                                  }`}
                                  style={{ 
                                    width: `${Math.min(Math.abs((newAmount / (previousAmount || 1)) * 100), 100)}%`,
                                    marginLeft: newAmount < previousAmount ? `${100 - Math.min(Math.abs((newAmount / (previousAmount || 1)) * 100), 100)}%` : '0'
                                  }}
                                />
                              </div>
                            </div>

                            <div className="sm:col-span-3">
                              <label htmlFor="adjustment_date" className="block text-sm font-medium text-gray-700">
                                Adjustment Date <span className="text-red-500">*</span>
                              </label>
                              <div className="mt-1">
                                <input
                                  type="date"
                                  {...register('adjustment_date')}
                                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange sm:text-sm"
                                />
                              </div>
                              {errors.adjustment_date && (
                                <p className="mt-2 text-sm text-red-600">{errors.adjustment_date.message}</p>
                              )}
                            </div>

                            <div className="sm:col-span-6">
                              <label htmlFor="reason" className="block text-sm font-medium text-gray-700">
                                Reason for Adjustment <span className="text-red-500">*</span>
                              </label>
                              <div className="mt-1">
                                <textarea
                                  rows={3}
                                  {...register('reason')}
                                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange sm:text-sm"
                                  placeholder="Explain why this budget adjustment is necessary"
                                />
                              </div>
                              {errors.reason && (
                                <p className="mt-2 text-sm text-red-600">{errors.reason.message}</p>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                          <button
                            type="submit"
                            disabled={isSubmitting}
                            className="inline-flex w-full justify-center rounded-md border border-transparent bg-golf-orange px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-golf-orange/90 focus:outline-none focus:ring-2 focus:ring-golf-orange focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                          >
                            {isSubmitting ? 'Saving...' : 'Save Adjustment'}
                          </button>
                          <button
                            type="button"
                            className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-golf-orange focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                            onClick={onClose}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </Tab.Panel>
                    <Tab.Panel>
                      <div className="px-4 py-5 sm:p-6">
                        <h4 className="text-lg font-medium text-gray-900 mb-4">Adjustment History</h4>
                        
                        {adjustmentHistory.length === 0 ? (
                          <p className="text-gray-500 text-center py-4">No adjustment history found</p>
                        ) : (
                          <div className="space-y-4">
                            {adjustmentHistory.map((adjustment) => (
                              <div key={adjustment.id} className="bg-gray-50 rounded-lg p-4">
                                <div className="flex justify-between items-start mb-2">
                                  <div className="text-sm font-medium text-gray-900">
                                    {formatDate(adjustment.adjustment_date)}
                                  </div>
                                  <div className={`flex items-center ${
                                    adjustment.new_amount > adjustment.previous_amount
                                      ? 'text-green-600'
                                      : 'text-red-600'
                                  }`}>
                                    {adjustment.new_amount > adjustment.previous_amount ? (
                                      <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                      </svg>
                                    ) : (
                                      <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 17h8m0 0v-8m0 8l-8-8-4 4-6-6" />
                                      </svg>
                                    )}
                                    <span className="text-sm font-medium">
                                      {formatCurrency(adjustment.new_amount - adjustment.previous_amount)}
                                    </span>
                                  </div>
                                </div>
                                <div className="flex justify-between text-sm text-gray-500 mb-2">
                                  <div>Previous: {formatCurrency(adjustment.previous_amount)}</div>
                                  <div>New: {formatCurrency(adjustment.new_amount)}</div>
                                </div>
                                <div className="text-sm text-gray-600 mt-2">
                                  <strong>Reason:</strong> {adjustment.reason}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        <div className="mt-6 flex justify-end">
                          <button
                            type="button"
                            className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-golf-orange focus:ring-offset-2"
                            onClick={onClose}
                          >
                            Close
                          </button>
                        </div>
                      </div>
                    </Tab.Panel>
                  </Tab.Panels>
                </Tab.Group>

                <div className="absolute right-0 top-0 pr-4 pt-4 z-10">
                  <button
                    type="button"
                    className="rounded-full bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-golf-orange focus:ring-offset-2"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}